import React, { useCallback, useMemo } from "react";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import styles from "./DeviceConfigForm.module.css";
import { DeviceCategory, DeviceConfig } from "../../../services/types/device.types";
import {
  DEVICE_CATEGORIES,
  getDefaultDeviceConfig,
  isStimulationCategory,
  normalizeDeviceConfig,
} from "../../../utils/deviceConfiguration";
import { useAppSelector } from "../../../services/hooks";
import { adminSelector } from "../../../services/selectors/user.selectors";
import { appointmentsProgramsDataSelector } from "../../../services/selectors/utils.selectors";
import { useGetAllMetaTablesQuery } from "../../../services/api/utils.api";
import NeurofeedbackPanel from "./panels/NeurofeedbackPanel";
import LoretaPanel from "./panels/LoretaPanel";
import StimulationPanel from "./panels/StimulationPanel";

interface DeviceConfigFormProps {
  value: DeviceConfig;
  onChange: (next: DeviceConfig) => void;
  /** When set, category is fixed and the type selector is hidden (appointment session). */
  fixedCategory?: DeviceCategory;
}

function DeviceConfigForm({ value, onChange, fixedCategory }: DeviceConfigFormProps) {
  const config = useMemo(
    () => normalizeDeviceConfig(fixedCategory ? { ...value, category: fixedCategory } : value),
    [value, fixedCategory],
  );
  const category = config.category ?? "nf";
  const isScan = category === "scan";

  // The frequency catalogue is shared with the treatment plan, so it is read from the same meta.
  const admin = useAppSelector(adminSelector);
  useGetAllMetaTablesQuery(admin.companyId, { skip: !admin.companyId });
  const meta = useAppSelector(appointmentsProgramsDataSelector);

  const sequenceOptions = useMemo(
    () =>
      meta.AppointmentsSequence.map((s) => ({
        value: Number(s.value ?? s.Id),
        label: String(s.label ?? s.CountInPeriod),
      })),
    [meta.AppointmentsSequence],
  );

  const patchConfig = useCallback(
    (patch: Partial<DeviceConfig>) => {
      onChange(normalizeDeviceConfig({ ...config, ...patch, category }));
    },
    [config, category, onChange],
  );

  const handleCategoryChange = useCallback(
    (next: DeviceCategory) => {
      onChange(getDefaultDeviceConfig(next));
    },
    [onChange],
  );

  return (
    <div className={styles.form}>
      {!fixedCategory && (
        <div className={styles.field}>
          <label className={styles.label}>{t("DEVICE_TYPE")}</label>
          <select
            className={styles.select}
            value={category}
            onChange={(e) => handleCategoryChange(e.target.value as DeviceCategory)}
          >
            {DEVICE_CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {t(c.labelKey)}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* One frequency list for the whole system: the same rows the treatment plan offers, so a
          protocol can never prescribe a cadence the scheduler has no way to express. Optional —
          a protocol may leave the cadence to the person planning the series — and absent for a
          scan, which happens once and so has nothing to repeat on. */}
      {!isScan && (
      <div className={styles.field}>
        <label className={styles.label}>{t("APPOINTMENT_FREQUENCY")}</label>
        <select
          className={styles.select}
          value={config.appointmentsSequenceId ?? ""}
          onChange={(e) =>
            patchConfig({
              appointmentsSequenceId: e.target.value ? Number(e.target.value) : null,
            })
          }
        >
          <option value="">{t("SELECT_PLACEHOLDER")}</option>
          {sequenceOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      )}

      {/* A scan has no panel of its own on purpose: it measures rather than treats, so there is
          no montage to lay out, no length to fix and no cadence to set. Saying so is better than
          leaving the form looking unfinished. */}
      {isScan && <p className={styles.scanNote}>{t("DEVICE_SCAN_NO_CONFIG")}</p>}

      {category === "nf" && <NeurofeedbackPanel config={config} onChange={patchConfig} />}
      {category === "loreta" && <LoretaPanel config={config} onChange={patchConfig} />}
      {isStimulationCategory(category) && (
        <StimulationPanel category={category} config={config} onChange={patchConfig} />
      )}
    </div>
  );
}

export default DeviceConfigForm;
